import { BaseQueryFn, FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta } from '@reduxjs/toolkit/query';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getIsSigningOut, globalHeaders } from '../utils/apiFetch';
import { getSecrets } from '../utils/secretsManager';

// Cache for the API URL from secrets
let cachedApiUrl: string | null = null;
let apiUrlPromise: Promise<string> | null = null;

// Function to get and cache the API URL from secrets
const getApiUrl = async (): Promise<string> => {
	// If already cached, return immediately
	if (cachedApiUrl) {
		return cachedApiUrl;
	}

	// If a fetch is in progress, wait for it
	if (!apiUrlPromise) {
		apiUrlPromise = getSecrets()
			.then((secrets) => {
				cachedApiUrl = secrets.apiURL;
				apiUrlPromise = null;
				return cachedApiUrl;
			})
			.catch((error) => {
				apiUrlPromise = null;
				console.warn('Failed to get API URL from secrets:', error);
				// Fallback to environment variable or default
				cachedApiUrl = (import.meta.env.VITE_API_BASE_URL as string) || '/';
				return cachedApiUrl;
			});
	}

	return apiUrlPromise;
};

export const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, object, FetchBaseQueryMeta> = async (
	args,
	api,
	extraOptions
) => {
	// Prevent any API calls during signout
	if (getIsSigningOut()) {
		return {
			error: {
				status: 'CUSTOM_ERROR',
				error: 'Request aborted due to signout',
				data: null
			}
		};
	}

	// Get the API URL from secrets
	const apiUrl = await getApiUrl();

	const result = await fetchBaseQuery({
		baseUrl: apiUrl,
		prepareHeaders: (headers) => {
			// if (!prodCheck()) console.warn('Preparing API headers. Global headers:', globalHeaders);

			Object.entries(globalHeaders).forEach(([key, value]) => {
				headers.set(key, value);

				// if (!prodCheck()) console.warn(`Setting header ${key}:`, value.substring(0, 50) + '...');
			});

			// if (!prodCheck()) console.warn('Final headers:', Object.fromEntries(headers.entries()));

			return headers;
		}
	})(args, api, extraOptions);

	return result;
};

export const apiService = createApi({
	baseQuery,
	endpoints: () => ({}),
	reducerPath: 'apiService',
	tagTypes: ['Config']
});

export default apiService;